import React from 'react';
import { observer } from 'mobx-react';
import { Link } from 'react-router';

// Helpers
import { confirm, promptUser, openExamDirect } from '../../helpers/helpers.js'

// Translations
import translate from '../Translate.js';

// Dependencies
import moment from 'moment';
import { FeatureFlag } from "react-launch-darkly";

// Components
import * as icons from '../Icons/Icons';
import ExamDetails from '../Modules/ExamDetails';

const ExamCardScheduled = observer(class ExamCardScheduled extends React.Component {
  constructor() {
    super();

    // Bind this to functions
    this.setAccessCode = this.setAccessCode.bind(this);
    this.cancelSession = this.cancelSession.bind(this);
    this.startReschedule = this.startReschedule.bind(this);
    this.startSession = this.startSession.bind(this);
    this.setCancelReason = this.setCancelReason.bind(this);
    this.renderRBDisabled = this.renderRBDisabled.bind(this);
    this.renderRBEnabled = this.renderRBEnabled.bind(this);
    this.toggleChat = this.toggleChat.bind(this);
  }

  componentWillMount() {
    const { exam, store } = this.props;
    const { examSession } = exam;
    const now = moment(store.SessionStore.currentTime);

    this.state = {
      accessCode: null,
      examUrl: '',
      onboardingVersion: 'old',
      now
    }

    // If it’s a VTC session, we need to get the access code
    // because for some reason the API response doesn’t have it
    if(examSession.schedulingModule === 'vtc') {
      store.ExamStore.getExamSession(exam.sid, examSession.sid, this.setAccessCode)
    }

    // Get the current time
    // (Uses background loading to prevent re-rendering)
    const backgroundLoad = true;
    store.SessionStore.getCurrentTime(backgroundLoad);

    setInterval(() => {
      const {now: stateNow} = this.state;
      this.setState({now: moment(stateNow).add(30, 'seconds')});
    }, 30000);
  }

  componentDidMount() {
    const { exam, store } = this.props;
    const { SessionStore } = store;
    const { examSession } = exam;
    const { installSid } = SessionStore.userData;

    openExamDirect(installSid, exam.sid, examSession.sid)
      .then(response => {
        this.setState({ examUrl: response.redirectUrl, onboardingVersion: response.version });
      })
  }

  componentWillUnmount() {
    // Clean up interval when component unmounts
    if (this.reconnectTimer) {
      clearInterval(this.reconnectTimer);
    }
  }

  updateReconnectTime = () => {
    // Force re-render to update the countdown
    this.forceUpdate();
  };

  setAccessCode(response) {
    this.setState({
      accessCode: response.data.accessKey
    });
  }

  cancelSession(examSid, sessionSid, schedulingModule) {
    const { translation } = this.props;
    let msg = {};

    if(!this.props.schedulingClosed) {
      msg = {
        heading: translation.cancel_session.heading,
        body: translation.cancel_session.body,
        button: translation.cancel_session.button
      }
    } else {
      msg = {
        heading: translation.cancel_session_closed.heading,
        body: translation.cancel_session_closed.body,
        button: translation.cancel_session_closed.button
      }
    }

    confirm(msg, () => this.cancelSessionReason(examSid, sessionSid, schedulingModule));
  }

  cancelSessionReason(examSid, sessionSid, schedulingModule) {
    const { store, translation } = this.props;
    const { ScheduleStore } = store;

    const options = [
      {
        label: translation.cancel_options.helper,
        value: ''
      }, {
        label: translation.cancel_options.time.label,
        value: 'changed-time'
      }, {
        label: translation.cancel_options.technical.label,
        value: 'technical-issue'
      }, {
        label: translation.cancel_options.other.label,
        value: 'other'
      }
    ]

    const msg = {
      heading: translation.cancel_reason.heading,
      options,
      button: translation.cancel_reason.button
    }

    const selectFunc = this.setCancelReason;
    const callback = () => ScheduleStore.cancelSession(examSid, sessionSid, schedulingModule, null, this.props.routing);

    promptUser(msg, selectFunc, callback);
  }

  setCancelReason(cancellationMemo) {
    this.props.store.ScheduleStore.cancellationMemo = cancellationMemo;
  }

  startReschedule(e, schedulingModule, proctorLocationSid, examSid, examSessionSid, courseSid, tiered = false) {
    e.preventDefault();

    const _this = this;

    const callback = () => {
      if(
        schedulingModule === 'b-virtual' ||
        schedulingModule === 'examity' ||
        schedulingModule === 'proctoru'
      ) {
        _this.props.store.ProctorStore.proctorLocationSid = proctorLocationSid;
        _this.props.parent.setState({ proctorGroupSid: null });
        _this.props.toggleDrawer(null, _this.props.parent, 'ScheduleOnline');
      } else if (schedulingModule === 'smarter-scheduling' && tiered) {
        const proctorGroupSid = _this.props.exam.examSession.proctorGroupSid;
        _this.props.parent.setState({ proctorGroupSid });
        _this.props.toggleDrawer(null, _this.props.parent, 'ScheduleHybrid');
      } else if (schedulingModule === 'smarter-scheduling') {
        _this.props.parent.setState({
          proctorGroupSid: null,
          proctorLocationSid
        });
        _this.props.store.ProctorStore.proctorLocationSid = proctorLocationSid;
        _this.props.toggleDrawer(null, _this.props.parent, 'ScheduleOnline');
      } else if(schedulingModule === 'register-blast') {
        _this.props.parent.setState({ proctorGroupSid: null });
        _this.props.toggleDrawer(null, _this.props.parent, 'ScheduleRB');
      } else {
        _this.props.store.ExamStore.getExam(examSid, courseSid);
        _this.props.parent.setState({ proctorGroupSid: null });
        _this.props.toggleDrawer(null, _this.props.parent, 'ScheduleInstructor');
      }
    }

    this.props.store.ExamStore.getExamSession(examSid, examSessionSid, callback);

    this.props.parent.setState({
      examSid: this.props.exam.sid,
      sessionSid: this.props.exam.examSession.sid
    })
  }

  /**
   * Start an exam session
   *
   * @param {string} schedulingModule
   * @param {string} workflowModule
   */
  startSession(schedulingModule, workflowModule) {
    const { exam } = this.props;
    const examSid = exam.sid;
    const sessionSid = exam.examSession.sid;
    let drawerContent = null;

    this.props.parent.setState({
      exam,
      examSid,
      sessionSid
    });

    if(schedulingModule === 'b-virtual') {
      drawerContent = 'SessionStartBV';
    } else if(schedulingModule === 'proctoru') {
      drawerContent = 'SessionStartProctorU';
    } else if(schedulingModule === 'examity') {
      drawerContent = 'SessionStartExamity';
    } else if(schedulingModule === 'automated') {
      drawerContent = 'SessionStartAutomated';
    } else if(workflowModule === 'hybrid-scheduling') {
      drawerContent = 'SessionStartHybrid';
    } else if(schedulingModule === 'vtc') {
      drawerContent = 'SessionStartVTC';
    } else {
      drawerContent = 'SessionDetailStudent';
    }

    this.props.toggleDrawer(null, this.props.parent, drawerContent)
  }

  renderRBDisabled() {
    const { translation } = this.props;

    return (
      <span className="cta__subcopy">
        {translation.rb_disabled}
      </span>
    );
  }

  renderRBEnabled() {
    const { translation, exam } = this.props;

    return (
      <button
        className="cta__btn btn--outline btn--negative"
        onClick={() => {this.cancelSession(exam.sid, exam.examSession.sid, exam.examSession.schedulingModule)}}
      >
        <icons.iconTimesCircle />
        {translation.reschedule_cta.cancel_button}
      </button>
    )
  }

  toggleChat() {
    if(!this.state.chat) {
      this.setState({
        chat: true
      });
    } else {
      this.chat.show_full_view();
    }
  }

  renderMessageButton(examSid) {
    const { translation } = this.props;

    return (
      <button
        className="btn btn--inverse--success"
        onClick={() => {this.props.messageInstructor(examSid)}}
      >
        <icons.iconEnvelope />
        {translation.message_button}
      </button>
    )
  }

  renderRBReschedule(schedulingModule, locationSid, examSid, sessionSid, courseSid) {
    const { translation } = this.props;

    return (
      <button
        className="cta__btn btn--outline"
        onClick={(e) => {
          this.startReschedule(
            e,
            schedulingModule,
            locationSid,
            examSid,
            sessionSid,
            courseSid
          )
        }}
      >
        <icons.iconCalendar />
        {translation.reschedule_cta.reschedule_button}
      </button>
    )
  }

  renderSystemCheckAutomated(showButton) {
    const { translation } = this.props;
    
    if (showButton) {
      return (
        <div className="btn-group__no-flex">
          <a
            className="btn  btn--outline btn--icn-right"
            href={`${this.state.examUrl}&systemCheck=true`}
            target="_blank"
            rel="opener"
          >
            {translation.system_check}
            <icons.iconCircleRight />
          </a>

          <a
            className="btn btn--success btn--icn-right"
            href={this.state.examUrl}
            target="_top"
            rel="opener"
          >
            {translation.start_exam}
            <icons.iconCircleRight />
          </a>
        </div>
      );
    } else {
      return (
        <a
          className="btn btn--success btn--icn-right"
          href={this.state.examUrl}
          target="_top"
          rel="opener"
        >
          {translation.start_exam}
          <icons.iconCircleRight />
        </a>
      );

    }
  }

  renderSystemCheckHybrid(showButton, timeToSessionEnd, workflowModule) {
    const { exam, translation } = this.props;

    if (showButton && workflowModule !== 'hybrid-scheduling') {
      return (
        <div className="btn-group__no-flex">
          {/* Old onboarding, comment out system check 
          <a
            className="btn  btn--outline btn--icn-right"
            href={`${this.state.examUrl}&systemCheck=true`}
            target="_blank"
            rel="opener"
          >
            {translation.system_check}
            <icons.iconCircleRight />
          </a>
          */}
          
          { timeToSessionEnd > 0 &&
          <button
            className="btn btn--success btn--icn-right"
            onClick={() =>
              this.startSession(exam.examSession.schedulingModule, workflowModule)
            }
          >
            {translation.start_exam}
            <icons.iconCircleRight />
          </button>
          }

        </div>
      );
    } else if (timeToSessionEnd > 0) {
      return (
        <button
          className="btn btn--success btn--icn-right"
          onClick={() =>
            this.startSession(exam.examSession.schedulingModule, workflowModule)
          }
        >
          {translation.start_exam}
          <icons.iconCircleRight />
        </button>
      );
    } else {
      return null;
    }
  }

    RenderSessionPendingReconnect() {
    const { exam, translation } = this.props;
    const { examSession } = exam;
    const reconnection = examSession.series.reconnection;
    console.log(translation.session_pending_reconnect);
    // Calculate remaining time
    const now = moment().tz('UTC');
    const endTime = moment(reconnection.attemptUntilDate).tz('UTC');
    const duration = moment.duration(endTime.diff(now));
    
    // Start/update the timer if not already running
    if (!this.reconnectTimer && duration.asMilliseconds() > 0) {
      this.reconnectTimer = setInterval(this.updateReconnectTime, 1000);
    }
    
    // Format the remaining time
    const formatTime = (duration) => {
      if (duration.asMilliseconds() <= 0) {
        clearInterval(this.reconnectTimer);
        return '00:00:00';
      }
      
      const hours = Math.floor(duration.asHours());
      const minutes = duration.minutes();
      const seconds = duration.seconds();

      let _timeRemaining = '';
      if(hours > 0){
        _timeRemaining += `${hours} hour${hours > 1 ? 's' : ''} `;
      }
      if(minutes > 0){
        _timeRemaining += `${minutes} minute${minutes > 1 ? 's' : ''} `;
      }
      if(seconds > 0){
        _timeRemaining += `${seconds} second${seconds > 1 ? 's' : ''} `;
      }

      return _timeRemaining.trim();
    };

    const timeRemaining = formatTime(duration);
    
    if(duration.asMilliseconds() > 0){
      return (

        <div className="reconnect-timer" style={{ textAlign: 'center' }}>
          <strong className="cta__copy">
            {translation.session_pending_reconnect.title}
          </strong>
          <div className="reconnect-timer__label cta__subcopy">
            {translation.session_pending_reconnect.message}
          </div>
          <div className="reconnect-timer__label cta__subcopy">
            {translation.session_pending_reconnect.expires.replace('%timeRemaining%', timeRemaining)}
          </div>
          
          <a
            className="btn btn--success btn--icn-right"
            href={this.state.examUrl}
            target="_top"
            rel="opener"
          >
            {translation.session_pending_reconnect.button}
            <icons.iconCircleRight />
          </a>
        </div>
      );
    } else {
      return (
        <div className="reconnect-timer" style={{ textAlign: 'center' }}>
          <strong className="cta__copy">
            {translation.session_pending_reconnect.title_expired}
          </strong>
          <div className="reconnect-timer__label cta__subcopy">
            {translation.session_pending_reconnect.message_expired}
          </div>
        </div>
      );
    }
  }

  render() {
    const { translation, store, exam } = this.props;
    const { now } = this.state;
    const { location } = exam.examSession;
    const { userTimezone } = store.SessionStore;
    const { examConfiguration } = exam.examSession;
    let examOpen = false;
    let sessionInProgress = false;
    let sessionPendingReconnection = false;


    const sessionTime = moment(exam.examSession.appointmentDate);
    const sessionStartTime = moment(sessionTime).subtract(10, 'minutes');
    const formattedTime = moment.utc(sessionStartTime).tz(userTimezone).format('M/D/YYYY [at] h:mma z');
    const timeToSession = moment.duration(sessionTime.diff(now)).asMinutes();
    const sessionEnd = moment(sessionTime).add(examConfiguration.duration, 'minutes').add(30, 'minutes');
    const hybridSessionEnd = moment(sessionTime).add(30, 'minutes');
    const timeToSessionEnd = moment.duration(sessionEnd.diff(now)).asMinutes();
    const hybridTimeToSessionEnd = moment.duration(hybridSessionEnd.diff(now)).asMinutes();

    // Max and min dates, used to make sure rescheduling is still allowed
    const maxDate = moment(examConfiguration.examCloseDate).subtract(30, 'minutes').subtract(examConfiguration.duration, 'minutes').tz(userTimezone);

    let minDate = null;
    minDate = moment(examConfiguration.examOpenDate);
    const timeDiff = moment.duration(minDate.diff(now)).asHours();

    if(timeDiff < 0) {
      minDate = now;
    }

    // Make sure the maxDate isn’t before the minDate
    const maxDiff = moment.duration(maxDate.diff(minDate)).asMinutes();

    // Make sure the exam is open
    if(now.diff(moment(examConfiguration.examOpenDate)) >= 0) {
      examOpen = true;
    }

    let workflowModule = null;

    if(exam && exam.examSession && exam.examSession.location) {
      workflowModule = exam.examSession.location.workflowModule;
    }

    // If the exam session is automated or hybrid/hybrid scheduling
    // and the session’s status is In Progress, we don’t want to allow
    // the user to try to start again.
    if (
      exam &&
      exam.examSession &&
      exam.examSession.status.toLowerCase() === 'in progress' &&
      (
        exam.examSession.schedulingModule === 'automated' ||
        exam.examSession.location.workflowModule === 'hybrid' ||
        exam.examSession.location.workflowModule === 'hybrid-scheduling'
      )
    ) {
      sessionInProgress = true;
    }

    // If the exam session is automated or hybrid/hybrid scheduling
    // and the session’s status is Pending Reconnection, need to surface the reconnection link
    if (
      exam &&
      exam.examSession &&
      exam.examSession.status.toLowerCase() === 'pending reconnection' &&
      (
        exam.examSession.schedulingModule === 'automated' ||
        exam.examSession.location.workflowModule === 'hybrid' ||
        exam.examSession.location.workflowModule === 'hybrid-scheduling'
      )
    ) {
      sessionPendingReconnection = true;
    }

    return (
      <article className="panel">
        <header className="panel__header panel__header--success">
          <h3 className="panel__title">
            {exam.type.toLowerCase() === 'online' &&
              <i className="panel__icn">
                <icons.iconLaptop />
              </i>
            }

            {exam.type.toLowerCase() === 'written' &&
              <i className="panel__icn">
                <icons.iconPencilPaper />
              </i>
            }

            {exam.title}
          </h3>

          <div className="panel__tools">
            <FeatureFlag
              flagKey="message-instructor"
              renderFeatureCallback={() => this.renderMessageButton(exam.sid)}
            />
          </div>
        </header>

        <div className="panel__body">
          {/* Upcoming scheduled Hybrid Session */}
          {workflowModule === 'hybrid-scheduling' && now < sessionStartTime &&
            <div className="cta">
              <div>
                <strong className="cta__copy">
                  {translation.hybrid.upcoming_session}  {exam.examSession  ? `by ${exam.examSession.location.friendlyName}` : ""}
                </strong>

                <span className="cta__subcopy">
                  {translation.hybrid.start_exam_button_appear.replace('%sessionStartTime%', formattedTime)}
                </span>
              </div>
            </div>
          }

          {/* Missed scheduled Hybrid Session */}
          {workflowModule === 'hybrid-scheduling' && hybridTimeToSessionEnd <= 0 &&
            <div className="cta missed">
              <div>
                <strong className="cta__copy">
                  {translation.hybrid.missed_session} {exam.examSession  ? `by ${exam.examSession.location.friendlyName} ` : ""} {translation.hybrid.missed_session_2}
                </strong>

                <span className="cta__subcopy">
                  {translation.hybrid.missed_sessionB}
                </span>
              </div>
            </div>
          }

          {/* In-progress automated session */}
          {sessionInProgress &&
            <div className="cta">
              <div>
                <strong className="cta__copy">
                  {translation.in_progress.generic.heading}
                </strong>

                <span className="cta__subcopy">
                  {translation.in_progress.generic.body}
                </span>
              </div>
            </div>
          }

          {/* Session is pending reconnection */}
          {sessionPendingReconnection &&
            <div className="cta">
              <div>
                {this.RenderSessionPendingReconnect()}
              </div>
            </div>
          }

          <section className="panel__section">
            <h2 className="panel__heading">
              {sessionPendingReconnection ?
                translation.session_pending_reconnect.heading :
                translation.scheduled.heading
              }

              {/* Start Automated */}
              {/* New onboarding so add system check button */}
              {exam.examSession.schedulingModule === 'automated' && examOpen && !sessionInProgress && !sessionPendingReconnection && this.state.onboardingVersion === 'new' &&
                <FeatureFlag
                  flagKey="system-check"
                  renderFeatureCallback={()=>this.renderSystemCheckAutomated(true)}
                  renderDefaultCallback={()=>this.renderSystemCheckAutomated(false)}
                />
              }

              {/* Old onboarding, leave system check out */}
              {exam.examSession.schedulingModule === 'automated' && examOpen && !sessionInProgress && !sessionPendingReconnection && this.state.onboardingVersion !== 'new' &&
                <a
                  className="btn btn--success btn--icn-right"
                  href={this.state.examUrl}
                  target="_top"
                  rel="opener"
                >

                  {translation.start_exam}
                  <icons.iconCircleRight />
                </a>
              }

              {/* Start Hybrid Scheduled */}
              {/* New onboarding so add system check button */}
              {workflowModule === 'hybrid-scheduling' && now >= sessionStartTime && !sessionInProgress && !sessionPendingReconnection && this.state.onboardingVersion === 'new' &&
                <FeatureFlag
                  flagKey="system-check"
                  renderFeatureCallback={()=>this.renderSystemCheckHybrid(true, hybridTimeToSessionEnd, workflowModule)}
                  renderDefaultCallback={()=>this.renderSystemCheckHybrid(false, hybridTimeToSessionEnd, workflowModule)}
                />
              }
              {/* Old onboarding, comment out system check
              {workflowModule === 'hybrid-scheduling' && now < sessionStartTime &&
                <a
                  className="btn btn--outline btn--icn-right"
                  href={`${this.state.examUrl}&systemCheck=true`}
                  target="_blank"
                  rel="opener"
                >
                  {translation.system_check}
                  <icons.iconCircleRight />
                </a>
              }
              */}

              {/* Old onboarding, leave system check out */}
              {workflowModule === 'hybrid-scheduling' && now >= sessionStartTime && !sessionInProgress && !sessionPendingReconnection && this.state.onboardingVersion !== 'new' &&
                <button
                  className="btn btn--success btn--icn-right"
                  onClick={() => this.startSession(exam.examSession.schedulingModule, workflowModule)}
                  // Disables Start button if session was missed
                  disabled={timeToSessionEnd <= 0 ? true : false}
                >

                  {translation.start_exam}
                  <icons.iconCircleRight />
                </button>
              }

              {/* Start other sessions */}
              {
                (
                  exam.examSession.schedulingModule !== 'automated' &&
                  workflowModule !== 'hybrid-scheduling'
                ) &&

                <button
                  className="btn btn--success btn--icn-right"
                  onClick={() => {this.startSession(exam.examSession.schedulingModule)}}
                >
                  {
                    (
                      exam.examSession.schedulingModule === 'b-virtual' ||
                      exam.examSession.schedulingModule === 'examity' ||
                      exam.examSession.schedulingModule === 'proctoru'
                    ) &&
                    <span>{translation.start_exam}</span>
                  }

                  {
                    exam.examSession.schedulingModule !== 'b-virtual' &&
                    exam.examSession.schedulingModule !== 'examity' &&
                    exam.examSession.schedulingModule !== 'proctoru' &&
                    <span>{translation.session_details}</span>
                  }
                  <icons.iconCircleRight />
                </button>
              }
            </h2>

            <ul className="data">
              {
                exam.examSession.schedulingModule !== 'vtc' &&
                exam.examSession.schedulingModule !== 'automated' &&

                <li>
                  <strong className="subtitle">{translation.scheduled.date}</strong>
                  {moment.utc(exam.examSession.appointmentDate).tz(userTimezone).format('M/D/YYYY [at] h:mma z')}
                </li>
              }
              {
                (
                  exam.examSession.schedulingModule === 'b-virtual' ||
                  exam.examSession.schedulingModule === 'examity' ||
                  exam.examSession.schedulingModule === 'proctoru' ||
                  exam.examSession.schedulingModule === 'automated' ||
                  workflowModule === 'hybrid-scheduling'
                ) &&

                <li>
                  <strong className="subtitle">{translation.scheduled.type}</strong>
                  {translation.scheduled[exam.examSession.schedulingModule] || translation.scheduled[workflowModule]}
                </li>
              }
              {exam.examSession.schedulingModule === 'vtc' &&
                <li>
                  <strong className="subtitle">{translation.scheduled.type}</strong>
                  {translation.scheduled.vtc}
                </li>
              }
              {exam.examSession.schedulingModule === 'instructor-as-proctor' &&
                <li>
                  <strong className="subtitle">{translation.scheduled.proctor}</strong>
                  {translation.scheduled.instructor}
                </li>
              }
              {
                exam.examSession.schedulingModule !== 'b-virtual' &&
                exam.examSession.schedulingModule !== 'examity' &&
                exam.examSession.schedulingModule !== 'proctoru' &&
                exam.examSession.schedulingModule !== 'vtc' &&
                exam.examSession.schedulingModule !== 'automated' &&
                workflowModule !== 'hybrid-scheduling' &&
                location &&

                <li className="adr">
                  <strong className="subtitle">{translation.scheduled.location}</strong>

                  {location.name &&
                    <span className="fn org">{location.name}</span>
                  }

                  {(location.address1 || location.address2) &&
                    <span className="street-address">
                      {location.address1}<br/>{location.address2 || ''}
                    </span>
                  }

                  {location.place &&
                    <span className="locality">{location.place}, </span>
                  }

                  {location.region &&
                    <span className="region">{location.region} </span>
                  }

                  {location.postalCode &&
                    <span className="postal-code">{location.postalCode}</span>
                  }

                  {location.country &&
                    <span className="country">{location.country}</span>
                  }
                </li>
              }
              {
                exam.examSession.schedulingModule !== 'b-virtual' &&
                exam.examSession.schedulingModule !== 'examity' &&
                exam.examSession.schedulingModule !== 'instructor-as-proctor' &&
                exam.examSession.schedulingModule !== 'proctoru' &&
                exam.examSession.schedulingModule !== 'vtc' &&
                workflowModule !== 'hybrid-scheduling' &&
                exam.examSession.proctor &&

                <li>
                  <strong className="subtitle">{translation.scheduled.proctor}</strong>
                  {exam.examSession.proctor.accountName &&
                    exam.examSession.proctor.accountName
                  }

                  {!exam.examSession.proctor.accountName && exam.examSession.proctor.firstName &&
                    exam.examSession.proctor.firstName
                  }
                </li>

              }
              {
                exam.examSession.schedulingModule !== 'b-virtual' &&
                exam.examSession.schedulingModule !== 'examity' &&
                exam.examSession.schedulingModule !== 'proctoru' &&
                !sessionInProgress &&
                !sessionPendingReconnection &&
                (timeToSession > 0 || exam.examSession.schedulingModule === 'smarter-scheduling') &&

                <li>
                  <div>
                  <strong className="subtitle">{translation.reschedule.heading}</strong>
                    <div className='flex'>
                    {
                      exam.examSession.schedulingModule !== 'ad-hoc' &&
                      exam.examSession.schedulingModule !== 'messaging' &&
                      exam.examSession.schedulingModule !== 'vtc' &&
                      exam.examSession.schedulingModule !== 'instructor-as-proctor' &&
                      exam.examSession.schedulingModule !== 'automated' &&
                      exam.examSession.schedulingModule !== 'register-blast' &&
                      //workflowModule !== 'hybrid-scheduling' &&
                      !this.props.schedulingClosed &&

                      <button
                        className="cta__btn btn--outline flex-1"
                        onClick={(e) => {
                          this.startReschedule(
                            e,
                            exam.examSession.schedulingModule,
                            exam.examSession.location.sid,
                            exam.sid,
                            exam.examSession.sid,
                            exam.courseSid,
                            workflowModule === 'hybrid-scheduling'
                          )
                        }}
                      >
                        <icons.iconCalendar />
                        {translation.reschedule.reschedule_button}
                      </button>
                    }

                    {
                      exam.examSession.schedulingModule === 'register-blast' &&
                      workflowModule !== 'hybrid-scheduling' &&
                      !this.props.schedulingClosed &&
                      <FeatureFlag
                        flagKey="register-blast-reschedule"
                        renderFeatureCallback={() => this.renderRBReschedule(
                          exam.examSession.schedulingModule,
                          exam.examSession.location.sid,
                          exam.sid,
                          exam.examSession.sid,
                          exam.courseSid
                        )}
                      />
                    }

                    {exam.examSession.schedulingModule !== 'register-blast' &&
                    <button
                      className="cta__btn btn--outline btn--negative flex-1"
                      onClick={() => {this.cancelSession(exam.sid, exam.examSession.sid, exam.examSession.schedulingModule)}}
                    >
                      <icons.iconTimesCircle />
                      {translation.reschedule.cancel_button}
                    </button>
                    }
                    {/* Cancel Button */}
                    {exam.examSession.schedulingModule === 'register-blast' &&
                    <FeatureFlag
                      flagKey="disable-register-blast"
                      renderFeatureCallback={this.renderRBDisabled}
                      renderDefaultCallback={this.renderRBEnabled}
                    />
                    }
                    </div>
                  </div>
                </li>
              }
            </ul>
          </section>



          <ExamDetails scheduleWindow={true} {...this.props} />



          {
            (
              exam.examSession.schedulingModule === 'b-virtual' ||
              exam.examSession.schedulingModule === 'examity' ||
              (
                exam.examSession.schedulingModule === 'proctoru' &&
                exam.examSession.status.toLowerCase() !== 'in progress'
              )
            ) &&
            <div className="cta">
              {timeToSession <= 0 &&
                timeToSession > -15 &&
                <div>
                  <strong className="cta__copy">
                    {translation.reschedule_cta.heading_late}
                  </strong>

                  <span className="cta__subcopy">
                    {translation.reschedule_cta.body_late.replace('%time%', moment.utc(exam.examSession.appointmentDate).tz(userTimezone).add(15, 'minutes').format('h:mma z'))}
                  </span>
                </div>
              }

              {(timeToSession > 0 || timeToSession < -15) &&
                <div>
                  <strong className="cta__copy">{translation.reschedule_cta.heading}</strong>

                  {
                    !this.props.schedulingClosed &&
                    maxDiff > 0 &&
                    <button
                      className="cta__btn btn--outline"
                      onClick={(e) => {
                        this.startReschedule(
                          e,
                          exam.examSession.schedulingModule,
                          exam.examSession.location.sid,
                          exam.sid,
                          exam.examSession.sid,
                          exam.courseSid
                        )
                      }}
                    >
                      <icons.iconCalendar />
                      {translation.reschedule_cta.reschedule_button}
                    </button>
                  }

                  <button
                    className="cta__btn btn--outline btn--negative"
                    onClick={() => {this.cancelSession(exam.sid, exam.examSession.sid, exam.examSession.schedulingModule)}}
                  >
                    <icons.iconTimesCircle />
                    {translation.reschedule_cta.cancel_button}
                  </button>
                </div>
              }
            </div>
          }

          {
            exam.examSession.schedulingModule === 'proctoru' &&
            exam.examSession.status.toLowerCase() === 'in progress' &&
            timeToSessionEnd > 0 &&
            <div className="cta">
              <strong className="cta__copy">{translation.in_progress.proctoru.heading}</strong>

              <span className="cta__subcopy">{translation.in_progress.proctoru.body}</span>

              <button
                className="cta__btn btn--outline btn--icn-right"
                onClick={() => {this.startSession(exam.examSession.schedulingModule)}}
              >
                {translation.in_progress.proctoru.button}
                <icons.iconCircleRight />
              </button>
            </div>
          }

          {
            exam.examSession.schedulingModule === 'proctoru' &&
            exam.examSession.status.toLowerCase() === 'in progress' &&
            timeToSessionEnd <= 0 &&
            <div className="cta">
              <strong className="cta__copy">{translation.done.proctoru.heading}</strong>

              <span className="cta__subcopy">{translation.done.proctoru.body}</span>

              <Link
                className="cta__btn btn--outline btn--icn-right"
                to="support/add"
              >
                {translation.done.proctoru.button}
                <icons.iconCircleRight />
              </Link>
            </div>
          }
        </div>
      </article>
    );
  }
})

export default translate('ExamCardScheduled')(ExamCardScheduled);



// WEBPACK FOOTER //
// ./src/app/components/StudentDashboard/ExamCardScheduled.js